import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { Tooltip } from '@material-ui/core';
import { TabsLayout } from '@cube3/ui/src/Tabs/TabsLayout';
import { TabView } from '@cube3/ui/src/Tabs/TabView';
import { UploadQueue } from './UploadQueue';
import { useUploads } from '@cube3/state/src/redux/components/Hooks/useUploads';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { queueTabs, toggleMediaqueue } from '@cube3/state/src/redux/ducks/ui';
import { Typography } from '@cube3/ui/src/typography/Typography';
import { StatusBlip } from '@cube3/ui/src/StatusBlip';
import { mediaQueueContext } from '../context/mediaQueueContext';
import { ExportQueue } from '../../Exports/ExportQueue';
import { useFeatures } from '@cube3/state/src/redux/components/Hooks/useFeatures';
import { Feature } from '@cube3/common/model/resource-types';

import { Scrollbar } from '@cube3/cubicle/src/core/atoms/Scrollbar';
import { WorkspaceActivity } from '../../ActvityFeeds/WorkspaceActivity';
import { useMenuToggle } from '@cube3/cubicle/src/helpers/hooks/useMenuToggle';
import useOutsideClick from '@cube3/cubicle/src/helpers/hooks/UseOutsideClick';
import { ContextMenu } from '@cube3/cubicle/src/core/molecules/ContextMenu';
import { Divider } from '@cube3/cubicle/src/core/atoms/Divider/Divider';
import { Button } from '@cube3/cubicle/src/core/atoms/Button/Button';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { MenuItem } from '@cube3/cubicle/src/core/atoms/MenuItem';
import { isMobile } from 'react-device-detect';
import { MobileSlideSurface } from '@cube3/cubicle/src/components/Surface/MobileSlideSurface';
import { USE_UPLOADS_VERSION_2 } from '@cube3/common/model/schema';

const uploadsTab = {
  label: 'Uploads',
  disabled: false,
  id: queueTabs.UPLOADS
};

const downloadsTab = {
  label: 'Downloads',
  disabled: true,
  disabledReason: 'Coming 2022'
};

const exportsTab = {
  label: 'Exports',
  disabled: false,
  id: queueTabs.EXPORTS
};

const feedTab = {
  label: 'Feed',
  disabled: false,
  id: queueTabs.FEED
};

const tabs = [uploadsTab];

const requiredExportFeatures = {
  features: [
    '/WORKSPACE/EXPORTS/YOUTUBE',
    '/WORKSPACE/EXPORTS/BROADCAST',
    '/WORKSPACE/ACTIVITIES'
  ] as Feature[]
};

interface MediaQueueProps {
  handleClose?: () => void;
}
const MediaQueue = React.memo((props: MediaQueueProps) => {
  const { handleClose } = props;
  const dispatch = useDispatch();
  const [hasYoutubeExport, hasBroadcastFeature, hasActivitiesFeature] =
    useFeatures(requiredExportFeatures);

  const tab =
    useTypedSelector((state) => state.ui.mediaQueueTab) || queueTabs.UPLOADS;

  const availableTabs = useMemo(
    () =>
      [
        ...tabs,
        (hasYoutubeExport || hasBroadcastFeature) && exportsTab,
        hasActivitiesFeature && feedTab
      ].filter((t) => !!t),
    [hasBroadcastFeature, hasYoutubeExport, hasActivitiesFeature]
  );

  const activeTab = availableTabs.find(
    (t) => t?.label?.toLowerCase() === tab?.toLowerCase()
  );

  /** open media queue */

  const toTab = useRef(undefined);

  const handleTabChange = useCallback(
    (tab, tabIndx, actions) => {
      actions.cancel();
      if (toTab.current === undefined) {
        toTab.current = tab;
        dispatch(
          toggleMediaqueue(
            true,
            availableTabs.find((t) => t.label === tab.label)?.id ||
            queueTabs.UPLOADS
          )
        );
        actions.resume();
      }
    },
    [dispatch, availableTabs]
  );

  useEffect(() => {
    if (toTab.current !== undefined && activeTab === toTab.current) {
      toTab.current = undefined;
    }
  }, [activeTab]);

  return (
    <div>
      <TabsLayout
        tabs={availableTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      >
        <TabView forTab={uploadsTab}>
          <UploadQueue />
        </TabView>

        <TabView forTab={downloadsTab}>
          <Typography typographyStyle="heading2">Downloads</Typography>
        </TabView>

        <TabView forTab={exportsTab}>
          <ExportQueue />
        </TabView>

        <TabView forTab={feedTab}>
          <Scrollbar>
            <WorkspaceActivity onClickLink={handleClose} />
          </Scrollbar>
        </TabView>
      </TabsLayout>
      <Divider />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '16px 8px 8px'
        }}
      >
        <Button
          label="Done"
          onClick={handleClose}
          background="primary"
          buttonStyle="solid"
          size={isMobile ? SizeVariants.md : SizeVariants.sm}
        />
      </div>
    </div>
  );
});

export const MediaQueueIndicator = React.memo(() => {
  const dispatch = useDispatch();

  const open = useTypedSelector((state) => state.ui.mediaQueueToggled) || false;

  /** open media queue */
  const handleClick = useCallback(() => {
    dispatch(toggleMediaqueue(true));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(toggleMediaqueue(false));
    toTab.current = undefined;
  }, [dispatch]);

  const toggleMenu = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      handleClick();
    }
  }, [open, handleClick, handleClose]);

  const toTab = useRef(undefined);

  useEffect(() => {
    window.addEventListener('dragenter', handleClose);
    return () => window.removeEventListener('dragenter', handleClose);
  }, [handleClose]);

  const { setAnchor, anchorEl } = useMenuToggle();
  const ref = useOutsideClick(handleClose);

  //failed status
  const { status, message, handleStatusChange } = useContext(mediaQueueContext);
  const { allUploads } = useUploads();

  const failed = useMemo(() => {
    return allUploads.filter((u) => u.failed || (u.active && !u.tusUpload && u['status'] !== 'succeeded'));
  }, [allUploads]);

  useEffect(() => {
    if (failed.length) {
      handleStatusChange({
        status: 'failed',
        message: `${failed.length} asset${failed.length !== 1 ? 's' : ''
          } failed to upload`
      });
    } else {
      handleStatusChange({
        status: null,
        message: null
      });
    }
  }, [failed, handleStatusChange]);

  return (
    <div>
      <Tooltip
        title={`${status === 'failed' ? `Media queue - ${message}` : ''}`}
        placement="left"
      >
        <div
          ref={setAnchor}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <MenuItem
            value="Queue"
            itemIcon="queue"
            onClick={toggleMenu}
            selected={open}
            failedStatus={status === 'failed'}
          />
          {status === 'failed' && (
            <div style={{ position: 'absolute', right: 0, paddingRight: 8 }}>
              <StatusBlip size="tiny" color="danger" />
            </div>
          )}

        </div>
      </Tooltip>
      {isMobile ? (
        <>
          {open && (
            <MobileSlideSurface onClose={handleClose}>
              <MediaQueue handleClose={handleClose} />
            </MobileSlideSurface>
          )}
        </>
      ) : (
        <>
          {anchorEl && open && (
            <ContextMenu
              anchor={anchorEl}
              placement="right-start"
              width={'404px'}
              popupRef={ref}
              scrollbarHeight={700}
            >
              <MediaQueue handleClose={handleClose} />
            </ContextMenu>
          )}
        </>
      )}
    </div>
  );
});
